import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { api, errMsg, fmtDT, fmtDate, fmtLeft, lim } from "../lib/api";

const TONES = {
  available: "bg-zinc-100 text-zinc-700", team_allocated: "bg-blue-100 text-blue-800", assigned: "bg-indigo-100 text-indigo-800",
  funded: "bg-emerald-100 text-emerald-800", pending: "bg-amber-100 text-amber-800", approved: "bg-blue-100 text-blue-800",
  waiting_inventory: "bg-amber-100 text-amber-800", fulfilled: "bg-emerald-100 text-emerald-800", rejected: "bg-rose-100 text-rose-800",
  queued: "bg-zinc-100 text-zinc-700", processing: "bg-blue-100 text-blue-800", completed: "bg-emerald-100 text-emerald-800",
  partially_failed: "bg-amber-100 text-amber-800", failed: "bg-rose-100 text-rose-800", expired: "bg-rose-100 text-rose-800",
  active: "bg-emerald-100 text-emerald-800", open: "bg-amber-100 text-amber-800", not_pushed: "bg-zinc-100 text-zinc-600",
  not_configured: "bg-zinc-200 text-zinc-700", uploaded: "bg-zinc-100 text-zinc-700", monitored_only: "bg-amber-100 text-amber-800",
  enforced_by_this_app: "bg-emerald-100 text-emerald-800", monitored: "bg-amber-100 text-amber-800", partial: "bg-amber-100 text-amber-800",
  unverified: "bg-zinc-200 text-zinc-700", provider_field_unverified: "bg-zinc-200 text-zinc-700",
};

export const StatusBadge = ({ s, label, testid }) => (
  <span data-testid={testid} className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${TONES[s] || "bg-zinc-100 text-zinc-700"}`}>
    {label || String(s || "unknown").replace(/_/g, " ")}
  </span>
);

const TIER_STYLE = {
  A: "bg-gradient-to-br from-[#f7d774] via-[#d4a017] to-[#a87b00] text-[#3b2a00] shadow-[0_8px_24px_-8px_rgba(212,160,23,0.8)] ring-[#f3d27a]",
  B: "bg-gradient-to-br from-[#7fd12b] via-[#58a80a] to-[#3f7d05] text-white shadow-[0_8px_24px_-8px_rgba(88,168,10,0.8)] ring-[#a5e06a]",
  C: "bg-gradient-to-br from-[#60a5fa] via-[#2563eb] to-[#1e40af] text-white shadow-[0_8px_24px_-8px_rgba(37,99,235,0.8)] ring-[#93c5fd]",
  D: "bg-gradient-to-br from-[#f87171] via-[#dc2626] to-[#991b1b] text-white shadow-[0_8px_24px_-8px_rgba(220,38,38,0.8)] ring-[#fca5a5]",
};

export const TierBadge = ({ tier, size = "lg", testid }) => !tier ? null : (
  <span data-testid={testid || "tier-badge"} className={`inline-flex items-center justify-center font-display font-black ring-2 ring-inset ${TIER_STYLE[tier]} ${
    size === "lg" ? "rounded-2xl px-5 py-2.5 text-2xl tracking-tight" : "rounded-full px-2.5 py-0.5 text-xs"}`}>Tier {tier}</span>
);


export const PageHeader = ({ title, sub, children }) => (
  <div className="flex flex-wrap items-end justify-between gap-4 mb-8 fade-in">
    <div>
      <div className="flex items-center gap-2 mb-2"><span className="h-1 w-6 rounded-full bg-[#58a80a]" /><span className="eyebrow">Choicelead.ai</span></div>
      <h1 className="text-3xl sm:text-4xl font-black text-zinc-950">{title}</h1>
      {sub && <p className="text-sm text-zinc-500 mt-2 max-w-2xl">{sub}</p>}
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);

export const Stat = ({ label, value, tone, testid }) => (
  <div className="panel p-5 relative overflow-hidden group" data-testid={testid}>
    <span className="absolute left-0 top-5 bottom-5 w-[3px] rounded-r bg-[#58a80a] scale-y-50 opacity-0 group-hover:scale-y-100 group-hover:opacity-100 transition-[transform,opacity] duration-300" />
    <div className="eyebrow">{label}</div>
    <div className={`font-display text-3xl font-bold mt-2 tracking-tight ${tone || "text-zinc-950"}`}>{value ?? "—"}</div>
  </div>
);

export const Meter = ({ label, allowance, used, reserved, testid }) => {
  const remaining = allowance ? Math.max(0, allowance - used) : 0;
  const over = allowance != null && used > allowance ? used - allowance : 0;
  const pct = allowance ? Math.min(100, (used / allowance) * 100) : 0;
  return (
    <div data-testid={testid} className="space-y-1">
      <div className="flex justify-between text-xs"><span className="text-zinc-600">{label}</span>
        <span className="mono">{used?.toLocaleString() ?? 0} / {lim(allowance)}</span></div>
      <div className="h-2 bg-zinc-100 rounded-full overflow-hidden"><div className={`h-full rounded-full transition-[width] duration-700 ${over ? "bg-rose-500" : "bg-gradient-to-r from-[#3f7d05] to-[#6cc417]"}`} style={{ width: `${pct}%` }} /></div>
      <div className="flex justify-between text-[11px] text-zinc-500 mono">
        <span>remaining {remaining.toLocaleString()}{reserved != null ? ` · reserved ${reserved}` : ""}</span>
        {over > 0 && <span className="text-rose-600 font-semibold" data-testid={`${testid}-overage`}>over by {over}</span>}
      </div>
    </div>
  );
};

export const Pager = ({ page, total, size = 50, onPage }) => (
  <div className="flex items-center justify-between px-3 py-2 text-xs text-zinc-500 border-t">
    <span className="mono">{total} records · page {page} of {Math.max(1, Math.ceil(total / size))}</span>
    <div className="flex gap-2">
      <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => onPage(page - 1)} data-testid="pager-prev">Previous</Button>
      <Button size="sm" variant="outline" disabled={page * size >= total} onClick={() => onPage(page + 1)} data-testid="pager-next">Next</Button>
    </div>
  </div>
);

export const Empty = ({ text }) => <div className="p-8 text-center text-sm text-zinc-500" data-testid="empty-state">{text}</div>;

export const CopyCurl = ({ curl, testid }) => !curl ? null : (
  <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={testid}
    onClick={() => navigator.clipboard.writeText(curl).then(() => toast.success("curl copied — replace <YOUR_AUTH_KEY> with your Auth key"), () => window.prompt("Copy this curl:", curl))}>
    Copy curl</Button>
);


export function useLoad(fn, deps = []) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const reload = async () => {
    try { setData(await fn()); setErr(null); } catch (e) { setErr(errMsg(e)); }
  };
  useEffect(() => { reload(); }, deps); // eslint-disable-line react-hooks/exhaustive-deps
  return [data, reload, err, setData];
}

export function FundDialog({ lead, open, onClose, onDone }) {
  const [f, setF] = useState({ funding_date: "", amount: "", lender: "", notes: "" });
  const submit = async () => {
    try {
      await api.post(`/leads/${lead.id}/fund`, { ...f, amount: f.amount ? Number(f.amount) : null, funding_date: f.funding_date || null });
      toast.success("Marked as funded");
      onDone?.();
      onClose();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white" data-testid="fund-dialog">
        <DialogHeader><DialogTitle>Mark as Funded — {lead?.business_name || lead?.contact_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <label className="space-y-1">Funding date<input type="date" className="fld w-full" value={f.funding_date} onChange={(e) => setF({ ...f, funding_date: e.target.value })} data-testid="fund-date-input" /></label>
          <label className="space-y-1">Amount<input type="number" className="fld w-full" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="fund-amount-input" /></label>
          <label className="space-y-1 col-span-2">Lender<input className="fld w-full" value={f.lender} onChange={(e) => setF({ ...f, lender: e.target.value })} data-testid="fund-lender-input" /></label>
          <label className="space-y-1 col-span-2">Notes<textarea className="fld w-full h-20 py-2" value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="fund-notes-input" /></label>
        </div>
        <p className="text-xs text-zinc-500">Funded leads leave rotation permanently. Only the admin can reverse this.</p>
        <Button onClick={submit} className="bg-[#58a80a] hover:bg-[#3f7d05]" data-testid="fund-submit-btn">Confirm funded</Button>
      </DialogContent>
    </Dialog>
  );
}

export function LeadDetail({ id, onClose, isAdmin }) {
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!id) { setD(null); return; }
    api.get(`/leads/${id}`).then((r) => setD(r.data)).catch((e) => { toast.error(errMsg(e)); onClose(); });
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={!!id} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="lead-detail-dialog">
        <DialogHeader><DialogTitle>{d?.business_name || d?.contact_name || "Lead"}</DialogTitle></DialogHeader>
        {d && (
          <div className="space-y-4 text-sm">
            <div className="grid grid-cols-2 gap-2">
              {[["Contact", d.contact_name], ["Phone", d.phone], ["Email", d.email], ["Status", <StatusBadge key="s" s={d.status} />],
                ["Original lead date", d.original_lead_date || "—"], ["Uploaded", fmtDT(d.uploaded_at)], ["Team allocated", fmtDT(d.team_allocated_at)],
                ["Rep assigned", fmtDT(d.rep_assigned_at)], ["Expires", fmtDT(d.expires_at)], ["Suppressed", d.suppressed ? "Yes" : "No"]].map(([k, v]) => (
                <div key={k}><div className="text-[11px] uppercase text-zinc-500">{k}</div><div className="mono text-[13px]" data-testid={`lead-detail-${k.toLowerCase().replace(/ /g, "-")}`}>{v || "—"}</div></div>
              ))}
            </div>
            {d.funded && <div className="panel p-3 bg-emerald-50 text-emerald-900 text-xs">Funded {d.funded.funding_date} · {d.funded.amount ?? "amount n/a"} · {d.funded.lender || "lender n/a"} · marked by {d.funded.marked_by_name}</div>}
            {isAdmin && d.history && (
              <div><div className="text-[11px] uppercase text-zinc-500 mb-1">History</div>
                <ul className="text-xs mono space-y-1 max-h-48 overflow-y-auto">{d.history.slice().reverse().map((h, i) => <li key={i}>{fmtDT(h.at)} · {h.type} {h.team_id || ""} {h.reason || ""}</li>)}</ul>
                <div className="text-[11px] text-zinc-500 mt-2">Sources: {(d.sources || []).map((s) => `${s.filename} row ${s.row}`).join("; ")}</div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function LeadsTable({ items, onOpen, onFund, extra }) {
  if (!items?.length) return <Empty text="No leads to show." />;
  return (
    <div className="overflow-x-auto">
      <table className="dt" data-testid="leads-table">
        <thead><tr><th>Business</th><th>Contact</th><th>Phone</th><th>Email</th><th>Status</th><th>Expires</th><th /></tr></thead>
        <tbody>{items.map((l) => (
          <tr key={l.id} data-testid={`lead-row-${l.id}`}>
            <td className="font-medium">{l.business_name || "—"}</td><td>{l.contact_name || "—"}</td>
            <td className="mono">{l.phone_masked || "—"}</td><td className="mono">{l.email_masked || "—"}</td>
            <td><StatusBadge s={l.status} />{l.suppressed && <StatusBadge s="rejected" label="suppressed" />}</td>
            <td className="mono text-xs">{l.status === "funded" ? fmtDate(l.funded?.marked_at) : fmtDT(l.expires_at)}</td>
            <td className="text-right whitespace-nowrap space-x-1">
              <Button size="sm" variant="outline" onClick={() => onOpen(l.id)} data-testid={`lead-open-${l.id}`}>Open</Button>
              {onFund && l.status !== "funded" && <Button size="sm" className="bg-[#58a80a] hover:bg-[#3f7d05]" onClick={() => onFund(l)} data-testid={`lead-fund-${l.id}`}>Mark as Funded</Button>}
              {extra?.(l)}
            </td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

export const Countdown = ({ seconds }) => (
  <span className={`mono text-xs ${seconds <= 0 ? "text-rose-600" : seconds < 86400 ? "text-amber-600" : "text-zinc-600"}`}>{fmtLeft(seconds)}</span>
);
